/**
 * Shared OOXML relationship/theme-path resolution, used by both the font and
 * table-style readers (extracted to avoid duplicating the rels-walking logic).
 *
 * Every function returns `null` to mean "the relationship/part isn't present in
 * the deck" — an expected outcome (not every slide has a full layout → master →
 * theme chain), NOT an error; callers just stop resolving and fall back.
 */
import { strFromU8, type Unzipped } from "fflate";

/**
 * The absolute zip path (via `abs`) of the first relationship Target in
 * `relsPath` matching `targetRe`, or null when the .rels part / a match is absent.
 */
export function resolveRel(
  zip: Unzipped,
  relsPath: string,
  targetRe: RegExp,
  abs: (target: string) => string,
): string | null {
  const file = zip[relsPath];
  if (!file) return null;
  const xml = strFromU8(file);
  for (const tag of xml.match(/<Relationship\b[^>]*\/>/g) ?? []) {
    const m = /\bTarget="([^"]+)"/.exec(tag);
    if (m && targetRe.test(m[1]!)) return abs(m[1]!);
  }
  return null;
}

export interface ThemeChain {
  layoutPath: string | null;
  masterPath: string | null;
  themePath: string | null;
}

/**
 * Walk the rels chain slide → layout → master → theme for the given slide file
 * (e.g. "slide1.xml"). A missing link yields nulls from that point onward.
 */
export function resolveThemeChain(zip: Unzipped, slideFile: string): ThemeChain {
  const layoutPath = resolveRel(
    zip,
    `ppt/slides/_rels/${slideFile}.rels`,
    /slideLayout\d+\.xml/,
    (t) => `ppt/slideLayouts/${t.split("/").pop()}`,
  );
  if (!layoutPath) return { layoutPath: null, masterPath: null, themePath: null };
  const masterPath = resolveRel(
    zip,
    `ppt/slideLayouts/_rels/${layoutPath.split("/").pop()}.rels`,
    /slideMaster\d+\.xml/,
    (t) => `ppt/slideMasters/${t.split("/").pop()}`,
  );
  if (!masterPath) return { layoutPath, masterPath: null, themePath: null };
  const themePath = resolveRel(
    zip,
    `ppt/slideMasters/_rels/${masterPath.split("/").pop()}.rels`,
    /theme\d+\.xml/,
    (t) => `ppt/theme/${t.split("/").pop()}`,
  );
  return { layoutPath, masterPath, themePath };
}
